import React from 'react';

const TermsOfUsePage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-of-use-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Use</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: February 2026</p>

        <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using <strong>brantfordareahomes.ca</strong> (the &ldquo;Website&rdquo;), you agree
              to be bound by these Terms of Use. The Website is operated by <strong>Alexander Conti</strong>,
              Sales Representative &mdash; REALTOR® with{' '}
              <strong>Royal LePage Brant Realty, Brokerage</strong>. If you do not agree with these terms,
              please do not use the Website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Accuracy of Real Estate Information</h2>
            <p>
              The property listings, prices, taxes, square footage, lot dimensions, school zones, and other
              real-estate-related information on this Website are obtained from sources we believe to be
              reliable (including the Toronto Regional Real Estate Board, the Realtors Association of
              Hamilton-Burlington, MLS&reg; data feeds, builders, and public records), but we do not guarantee
              their accuracy, completeness, or timeliness.
            </p>
            <p>
              Listings change rapidly. A property may be sold, withdrawn, or have its price changed at any time
              without notice. The information on this Website is provided <strong>for informational purposes
              only</strong> and should not be relied on as the sole basis for any purchase, sale, financing, or
              other real estate decision. Always verify details directly with us or your representative before
              acting.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. No Realtor-Client Relationship</h2>
            <p>
              Browsing this Website, submitting a contact form, requesting a home evaluation, using the chat
              widget, accepting a phone call from us, or signing up for an account does{' '}
              <strong>not</strong> automatically create a formal Realtor-client relationship between you and
              Alexander Conti or Royal LePage Brant Realty, Brokerage.
            </p>
            <p>
              In Ontario, a Realtor-client relationship is established only by signing a written representation
              agreement (a Buyer Representation Agreement or Listing Agreement) in accordance with the{' '}
              <em>Trust in Real Estate Services Act, 2002 (TRESA)</em> &mdash; which replaced and modernized REBBA effective
              December 1, 2023 &mdash; and the rules of the Real Estate Council of Ontario (RECO). Until such an
              agreement is signed, any information you provide is treated under the Self-Represented Party
              service standard introduced under TRESA.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Permitted Use</h2>
            <p>You may use the Website for your personal, non-commercial purposes only. You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Scrape, harvest, copy, or systematically download listings, photos, descriptions, or other content from this Website by any automated means (including bots, spiders, or crawlers)</li>
              <li>Resell, republish, or redistribute any of our content without our prior written permission</li>
              <li>Submit false, misleading, or fraudulent contact information through any form on the Website</li>
              <li>Use the chat or phone systems to harass, threaten, or send unlawful content</li>
              <li>Attempt to interfere with, probe, or compromise the security of the Website or our systems</li>
              <li>Use the Website in violation of any applicable federal, provincial, or municipal law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. AI-Assisted Communications and Recording</h2>
            <p>
              Our Website uses AI-assisted tools (chat assistant and voice assistant named &ldquo;Lauren&rdquo;)
              that may send and receive text messages, place outbound phone calls, and send emails on our
              behalf. Phone calls placed or received through our system <strong>may be recorded</strong> for
              quality, training, and follow-up purposes. By engaging with our chat widget or accepting a phone
              call from our system, you consent to this recording and to your conversation being relayed to
              Alexander Conti for human follow-up.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. CASL — Email and Text Consent</h2>
            <p>
              By voluntarily submitting your contact information through any form, chat, or call on this
              Website, you provide your express consent under Canada&rsquo;s Anti-Spam Legislation (CASL) to
              receive electronic communications from Alexander Conti regarding properties, market updates,
              and our real estate services. You may withdraw your consent at any time by clicking the
              &ldquo;unsubscribe&rdquo; link in any email, replying STOP to any text message, visiting{' '}
              <a href="/email-preferences" className="text-red-600 hover:underline">your preferences page</a>,
              or emailing us at <a href="mailto:alexanderconti@brantfordareahomes.ca" className="text-red-600 hover:underline">alexanderconti@brantfordareahomes.ca</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Intellectual Property</h2>
            <p>
              All content on this Website (including text, photographs, video, design, branding, and software)
              is the property of Alexander Conti, Royal LePage Brant Realty, Brokerage, our licensors, or
              our content providers, and is protected by Canadian and international copyright and trademark
              laws. The Royal LePage marks are owned by Bridgemarq Real Estate Services Manager Limited and
              used under license.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. CREA Trademarks</h2>
            <p>
              The trademarks REALTOR&reg;, REALTORS&reg;, and the REALTOR&reg; logo are controlled by The
              Canadian Real Estate Association (CREA) and identify real estate professionals who are members of
              CREA. The trademarks MLS&reg;, Multiple Listing Service&reg;, and the associated logos identify
              professional services rendered by REALTOR&reg; members of CREA to effect the purchase, sale, and
              lease of real estate as part of a cooperative selling system.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Third-Party Links</h2>
            <p>
              The Website may contain links to third-party sites (mortgage calculators, builder websites,
              municipal information pages, etc.). We do not control and are not responsible for the content,
              privacy practices, or accuracy of any third-party website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Disclaimer of Warranties &amp; Limitation of Liability</h2>
            <p>
              The Website is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis without
              warranties of any kind, either express or implied. To the maximum extent permitted by law,
              Alexander Conti and Royal LePage Brant Realty, Brokerage shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages arising out of your access to or use of,
              or inability to use, the Website or any information obtained from it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. Governing Law</h2>
            <p>
              These Terms of Use are governed by the laws of the Province of Ontario and the federal laws of
              Canada applicable therein. Any dispute arising out of or in connection with these terms or your
              use of the Website shall be resolved exclusively in the courts located in Brantford, Ontario.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">12. Changes to These Terms</h2>
            <p>
              We may update these Terms of Use from time to time. The &ldquo;Last updated&rdquo; date at the
              top of this page indicates when the most recent change was made. Continued use of the Website
              after a change constitutes acceptance of the revised terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">13. Contact</h2>
            <p>Questions about these Terms of Use? Contact us:</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mt-3">
              <p className="font-semibold text-gray-900">Alexander Conti</p>
              <p>Sales Representative &mdash; REALTOR®</p>
              <p>Royal LePage Brant Realty, Brokerage</p>
              <p className="mt-2">Email: <a href="mailto:alexanderconti@brantfordareahomes.ca" className="text-red-600 hover:underline">alexanderconti@brantfordareahomes.ca</a></p>
              <p>Phone: <a href="tel:+12269344107" className="text-red-600 hover:underline">226-934-4107</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;
